import { useState } from 'react';
import useSWR from 'swr';
import dayjs from 'dayjs';
import { isAxiosError } from 'axios';
import { toast } from 'App';
import Card from 'components/card';
import { UpdatePermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { publicNotificationsService } from 'views/services/publicNotificationsService';
import {
  EPublicNotificationStatus,
  EPublicNotificationType,
  IPublicNotification,
} from 'views/types/publicNotifications';

const PAGE_SIZE = 20;

const headerClass =
  'border-b border-gray-200 pb-2 pr-3 text-start text-xs font-bold uppercase text-gray-600 dark:border-white/20 dark:text-white';
const cellClass = 'py-2 pr-3 text-sm text-navy-700 dark:text-white';

const TYPE_EMOJI: Record<EPublicNotificationType, string> = {
  [EPublicNotificationType.UPDATE]: '🔄',
  [EPublicNotificationType.ANNOUNCEMENT]: '📢',
  [EPublicNotificationType.FEATURE]: '✨',
};

const STATUS_CLASSES: Record<EPublicNotificationStatus, string> = {
  [EPublicNotificationStatus.SCHEDULED]:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  [EPublicNotificationStatus.SENDING]:
    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  [EPublicNotificationStatus.SENT]:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  [EPublicNotificationStatus.CANCELLED]:
    'bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300',
  [EPublicNotificationStatus.FAILED]:
    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const formatDateTime = (value: string | null) =>
  value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—';

interface IProps {
  refreshKey: number;
}

export const HistoryTable: React.FC<IProps> = ({ refreshKey }) => {
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading, mutate } = useSWR(
    ['/api/admin/public-notifications', page, refreshKey],
    () => publicNotificationsService.getList({ page, pageSize: PAGE_SIZE }),
    { refreshInterval: 5000 }
  );

  const rows: IPublicNotification[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleCancel = async (row: IPublicNotification) => {
    if (
      !window.confirm(`Cancel the scheduled notification "${row.subject}"?`)
    ) {
      return;
    }

    try {
      setBusyId(row.id);
      await publicNotificationsService.cancel(row.id);
      await mutate();
      toast({
        title: 'Notification cancelled',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data?.message ?? error.message)
        : 'Failed to cancel';
      toast({
        title: 'Cancel failed',
        description: String(message),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card extra="p-4">
      <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
        History
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={headerClass}>Type</th>
              <th className={headerClass}>Subject</th>
              <th className={headerClass}>Status</th>
              <th className={headerClass}>Scheduled</th>
              <th className={headerClass}>Progress</th>
              <th className={headerClass}>Finished</th>
              <th className={headerClass}>Created</th>
              <th className={headerClass}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id}>
                <td className={cellClass}>
                  {TYPE_EMOJI[row.type]}{' '}
                  {row.type.charAt(0) + row.type.slice(1).toLowerCase()}
                </td>
                <td
                  className={`${cellClass} max-w-[280px] truncate`}
                  title={row.body}
                >
                  {row.subject}
                </td>
                <td className={cellClass}>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLASSES[row.status]}`}
                  >
                    {row.status}
                  </span>
                </td>
                <td className={cellClass}>{formatDateTime(row.scheduledAt)}</td>
                <td className={cellClass}>
                  {row.status === EPublicNotificationStatus.SCHEDULED ||
                  row.status === EPublicNotificationStatus.CANCELLED
                    ? '—'
                    : `${row.sentCount} sent${
                        row.failedCount ? ` / ${row.failedCount} failed` : ''
                      } of ${row.totalUsers ?? '?'}`}
                </td>
                <td className={cellClass}>{formatDateTime(row.finishedAt)}</td>
                <td className={cellClass}>{formatDateTime(row.createdAt)}</td>
                <td className={cellClass}>
                  {row.status === EPublicNotificationStatus.SCHEDULED && (
                    <UpdatePermissionGate
                      resource={PERMISSION_RESOURCES.PUBLIC_NOTIFICATIONS}
                    >
                      <button
                        disabled={busyId === row.id}
                        onClick={() => handleCancel(row)}
                        className="rounded bg-red-500 px-2 py-1 text-xs text-white disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </UpdatePermissionGate>
                  )}
                </td>
              </tr>
            ))}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td
                  className="py-4 text-center text-sm text-gray-500"
                  colSpan={8}
                >
                  No notifications yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-white">
          {total} notification(s) · page {page + 1} / {pageCount}
        </span>
        <div className="flex gap-2">
          <button
            disabled={page <= 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
          >
            Prev
          </button>
          <button
            disabled={page + 1 >= pageCount}
            onClick={() => setPage(p => p + 1)}
            className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
          >
            Next
          </button>
        </div>
      </div>
    </Card>
  );
};
